<script setup lang="ts">
import { computed, inject } from "vue";
import { FormElementBase, FormElementType } from "~/api";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import { getAnswerHasAnyValue } from "../../composables/useFormResponse/rulesEngine";
import { FormDisplayMode } from "../../types/FormDisplayMode";
import { formComponentDict } from "./formComponentDict";
import Tooltip from "./Tooltip.vue";

export interface DefaultFormComponentProps<T extends FormElementBase> {
  modelValue: T;
  isReadOnly: boolean;
  displayMode: FormDisplayMode;
  showOptionValues?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue: FormElementBase;
    showQuestionCodes?: boolean;
    displayMode?: FormDisplayMode;
    showArchived?: boolean;
  }>(),
  {
    showQuestionCodes: false,
    displayMode: FormDisplayMode.EDIT,
    showArchived: false,
  },
);

const { isHidden, isReadOnly, substituteTemplatedText, useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;

const mappedComponentProps = computed<
  DefaultFormComponentProps<FormElementBase>
>(() => ({
  modelValue: {
    ...props.modelValue,
    label: substituteTemplatedText(props.modelValue.label),
  },
  displayMode: props.displayMode,
  isReadOnly:
    isReadOnly(props.modelValue) ||
    props.displayMode === FormDisplayMode.READ_ONLY ||
    !!props.modelValue.isArchived,
}));

/**
 * Computed property to determine if the red asterisk should be shown
 * Shows asterisk when:
 * 1. showOptionality is enabled
 * 2. The question is required (not optional)
 * 3. The question does not have an answer yet
 */
const shouldShowRequiredAsterisk = computed(() => {
  const element = props.modelValue;

  // Only show if optionality display is enabled
  if (!element.properties.showOptionality) {
    return false;
  }

  // Only show for required (not optional) questions
  if (element.properties.isOptional) {
    return false;
  }

  // If there's an answer, hide the asterisk
  if (getAnswerHasAnyValue(useAnswer(element).value, element.type)) {
    return false;
  }

  // Show asterisk for required unanswered questions
  return true;
});
</script>

<template>
  <div
    v-if="!isHidden(modelValue) && (showArchived || !modelValue.isArchived)"
    :id="`${modelValue.guid}-${modelValue.repeaterGuid}`"
    :key="modelValue.guid || modelValue.questionCode"
    class="component-renderer"
    :class="{
      'standard-indent':
        modelValue.type !== FormElementType.SECTION &&
        modelValue.type !== FormElementType.HEADING,
      'component-indent-level-0': modelValue.properties.indentLevel === 0,
      'component-indent-level-1': modelValue.properties.indentLevel === 1,
      'component-indent-level-2': modelValue.properties.indentLevel === 2,
      'component-indent-level-3': modelValue.properties.indentLevel === 3,
      'component-indent-level-4': modelValue.properties.indentLevel === 4,
      'component-indent-level-5': modelValue.properties.indentLevel === 5,
      'table-save-indicator': modelValue.properties.saveToTableName !== null,
      //'has-tooltip': modelValue.properties.title,
    }"
  >
    <div v-if="showQuestionCodes" class="component-question-code">
      {{ modelValue.questionCode }}

      <span class="archived">
        {{ modelValue.isArchived ? "(archived)" : "" }}
      </span>
    </div>
    <component
      v-bind="mappedComponentProps"
      :is="formComponentDict[modelValue.type]"
      :show-option-values="showQuestionCodes"
    >
      <template #label>
        <div v-if="mappedComponentProps.modelValue.label">
          <div
            class="component-label-comment"
            :class="{
              'comment-below':
                mappedComponentProps.modelValue.properties.showCommentBelow,
            }"
          >
            <!-- eslint-disable-next-line vue/no-v-html  -->
            <div class="label" v-html="mappedComponentProps.modelValue.label" />
            <!-- Show red asterisk for required questions that don't have an answer -->
            <span
              v-if="shouldShowRequiredAsterisk"
              class="component-label-required"
              >*</span
            >
            <!-- Show optional indicator for optional questions when optionality is enabled -->
            <span
              v-else-if="
                modelValue.properties.showOptionality &&
                modelValue.properties.isOptional
              "
              class="component-label-optional"
            >
              (optional)
            </span>
            <Tooltip
              v-if="modelValue.properties.title"
              :model-value="modelValue"
            />

            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="mappedComponentProps.modelValue.properties.comment"
              class="component-comment"
              v-html="mappedComponentProps.modelValue.properties.comment"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>
      </template>
    </component>
    <!-- <div v-if="modelValue.properties.title" class="tooltip-icon">
      <Tooltip :model-value="modelValue" />
    </div> -->
  </div>
</template>

<style lang="less">
.component-renderer {
  display: flex;
  flex-direction: column;
  padding: 3px 8px;
  position: relative;

  // &.standard-indent {
  //   // padding-left: 8px;
  //   // padding-right: 40px;
  // }

  &.component-indent-level-0 {
    margin-left: 0;
    // padding-right: 40px;
  }
  &.component-indent-level-1 {
    margin-left: 16px;
  }
  &.component-indent-level-2 {
    margin-left: 32px;
  }
  &.component-indent-level-3 {
    margin-left: 48px;
  }
  &.component-indent-level-4 {
    margin-left: 64px;
  }
  &.component-indent-level-5 {
    margin-left: 80px;
  }

  .form-designer &.table-save-indicator {
    border-left: 4px solid var(--el-color-primary);
    padding-left: 4px;
  }
  // &.has-tooltip {
  // padding-right: 40px;
  // }
  // .el-tooltip__trigger {
  //   margin: 4px 0 0 7px;
  // }
  // .tooltip-icon {
  //   position: absolute;
  //   top: 0.5em;
  //   right: -4px;
  //   z-index: 1;

  //   .tooltip {
  //     display: flex;
  //     align-items: center;
  //     justify-content: center;

  //     .el-icon {
  //       font-size: 16px;
  //       cursor: pointer;
  //     }
  //   }
  // }

  .component-label-comment {
    display: flex;
    column-gap: 0.5rem;
    row-gap: 0;
    flex-wrap: wrap;
    align-items: baseline;
    .label {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .component-comment {
      font-style: normal;
      font-size: 0.85em;
      p {
        margin: 0;
      }
    }

    &.comment-below {
      .component-comment {
        flex-basis: 100%;
      }
    }
  }

  .component-label-optional {
    color: #999;
    font-style: italic;
    display: inline;
    margin-left: 4px;
  }

  .component-comment {
    opacity: 0.75;
    margin-left: 1em;
    display: inline-block;
  }

  .component-label-required {
    color: red;
    display: inline;
    margin-left: 2px;
  }

  .component-question-code {
    position: absolute;
    border: 1px solid var(--el-color-danger);
    width: fit-content;
    padding: 0px 6px;
    margin: -8px 0 0 -6px;
    font-size: 0.8em;
    background-color: var(--el-color-info-light-9);
    color: var(--el-text-color-primary);
    border-radius: 0.5em;
    .archived {
      font-style: italic;
      color: gray;
    }
  }

  .el-input__inner {
    text-align: right;
  }
}
</style>
